import React, { useState, useEffect } from 'react';
import {
  Card, Form, Input, Select, Button, Table, Tag,
  Popconfirm, message, Divider, DatePicker, Switch, Alert, Typography
} from 'antd';
import { SendOutlined, DeleteOutlined, BellOutlined, BellFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getBranches } from '../../api/branches';
import { sendNotification, getAdminNotifications, deleteNotification } from '../../api/notifications';
import api from '../../api/axios';
import UserSearchSelect from '../common/UserSearchSelect';
import usePersistedPageSize from '../../hooks/usePersistedPageSize';
import { apiErrorMessage } from '../../utils/apiError';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const TARGET_LABELS = {
  all_users:       'Tüm Kullanıcılar',
  teachers:        'Öğretmenler',
  parents:         'Veliler',
  branch_managers: 'Şube Müdürleri',
  interns:         'Stajyerler',
  branch:          'Şube',
  specific_user:   'Belirli Kullanıcı',
};

const TYPE_COLORS = {
  manual:              'blue',
  certificate_issued:  'green',
  attendance_present:  'cyan',
  attendance_absent:   'red',
  payment_received:    'gold',
  announcement:        'purple',
};

const NotificationSendTab = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [sending, setSending] = useState(false);
  const [targetType, setTargetType] = useState(null);
  const [branches, setBranches] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('adminNotifications', 15);
  const [pagination, setPagination] = useState({ current: 1, pageSize: persistedPageSize, total: 0 });
  const [dateRange, setDateRange] = useState(null);
  const [filterUserId, setFilterUserId] = useState(null);
  const [filterSearch, setFilterSearch] = useState('');
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    getBranches({ per_page: 100 }).then(res => setBranches(res.data.data || [])).catch(() => {});
    fetchNotifications(1);
    api.get('/settings').then(res => setNotificationsEnabled(res.data.notifications_enabled ?? true)).catch(() => {});
  }, []);

  const handleToggle = async (checked) => {
    setToggling(true);
    try {
      await api.put('/settings', { notifications_enabled: checked });
      setNotificationsEnabled(checked);
      message.success(checked ? 'Bildirimler açıldı.' : 'Bildirimler kapatıldı.');
    } catch {
      message.error('Ayar güncellenemedi.');
    } finally {
      setToggling(false);
    }
  };

  // overrides: filtre state'i setState ile guncellendigi anda (asenkron)
  // hemen ardindan fetchNotifications cagirilirsa, closure icindeki state
  // hala eski deger olur - degisen filtreyi burada acikca gecirerek bu
  // stale-closure hatasindan kaciniliyor.
  const fetchNotifications = async (page = 1, overrides = {}, perPage = pagination.pageSize) => {
    setLoading(true);
    const range = 'dateRange' in overrides ? overrides.dateRange : dateRange;
    const userId = 'filterUserId' in overrides ? overrides.filterUserId : filterUserId;
    const search = 'filterSearch' in overrides ? overrides.filterSearch : filterSearch;
    try {
      const res = await getAdminNotifications({
        page,
        per_page: perPage,
        date_from: range?.[0]?.format('YYYY-MM-DD HH:mm:ss'),
        date_to: range?.[1]?.format('YYYY-MM-DD HH:mm:ss'),
        user_id: userId || undefined,
        search: search?.trim() || undefined,
      });
      setNotifications(res.data.data || []);
      setPagination(p => ({ ...p, current: page, pageSize: res.data.per_page ?? perPage, total: res.data.total }));
    } catch {
      message.error('Bildirimler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (values) => {
    setSending(true);
    try {
      const res = await sendNotification({
        target_type: values.target_type,
        user_ids:    values.user_ids,
        branch_id:   values.branch_id,
        title:       values.title,
        body:        values.body,
      });
      message.success(res.data.message);
      form.resetFields(['target_type', 'user_ids', 'branch_id', 'title', 'body']);
      setTargetType(null);
      fetchNotifications(1);
    } catch (error) {
      message.error(apiErrorMessage(error, 'Gönderim başarısız.'));
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteNotification(id);
      message.success('Bildirim silindi.');
      fetchNotifications(pagination.current);
    } catch {
      message.error('Silinemedi.');
    }
  };

  const columns = [
    {
      title: 'Alıcı',
      key: 'parent',
      render: (_, r) => r.parent ? (
        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/users/${r.parent.id}`)}>
          {r.parent.name} {r.parent.surname}
        </Button>
      ) : '—',
    },
    {
      title: 'Başlık',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'İçerik',
      dataIndex: 'body',
      key: 'body',
      width: 280,
      render: (v) => (
        <Text ellipsis={{ tooltip: v }} style={{ maxWidth: 280, display: 'inline-block' }}>
          {v}
        </Text>
      ),
    },
    {
      title: 'Tür',
      dataIndex: 'type',
      key: 'type',
      render: (v) => <Tag color={TYPE_COLORS[v] || 'default'}>{v}</Tag>,
    },
    {
      title: 'Okundu',
      dataIndex: 'read_at',
      key: 'read_at',
      render: (v) => v ? (
        <Tag color="green">{dayjs(v).format('DD.MM HH:mm')}</Tag>
      ) : (
        <Tag color="orange">Okunmadı</Tag>
      ),
    },
    {
      title: 'Gönderildi',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (v) => dayjs(v).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Gönderen',
      key: 'sent_by',
      render: (_, r) => r.sent_by ? (
        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/users/${r.sent_by.id}`)}>
          {r.sent_by.name} {r.sent_by.surname}
        </Button>
      ) : <span className="text-gray-400">Sistem</span>,
    },
    {
      title: '',
      key: 'actions',
      render: (_, r) => (
        <Popconfirm
          title="Bu bildirim silinsin mi?"
          onConfirm={() => handleDelete(r.id)}
          okText="Evet"
          cancelText="Hayır"
        >
          <Button icon={<DeleteOutlined />} danger size="small" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Card className="mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {notificationsEnabled ? (
              <BellFilled style={{ fontSize: 20, color: '#52c41a' }} />
            ) : (
              <BellOutlined style={{ fontSize: 20, color: '#ff4d4f' }} />
            )}
            <div>
              <div className="font-semibold">Bildirim Sistemi</div>
              <div className="text-xs text-gray-500">
                {notificationsEnabled
                  ? 'Açık — tüm otomatik ve manuel bildirimler gönderiliyor'
                  : 'Kapalı — hiçbir bildirim gönderilmiyor'}
              </div>
            </div>
          </div>
          <Switch
            checked={notificationsEnabled}
            loading={toggling}
            onChange={handleToggle}
            checkedChildren="Açık"
            unCheckedChildren="Kapalı"
            style={{ backgroundColor: notificationsEnabled ? '#52c41a' : '#ff4d4f' }}
          />
        </div>
        {!notificationsEnabled && (
          <Alert
            className="mt-3"
            type="warning"
            showIcon
            message="Bildirimler kapalı. Yoklama, sertifika ve diğer otomatik bildirimler gönderilmeyecek."
          />
        )}
      </Card>

      <Card title="Bildirim Gönder" className="mb-6">
        <Form form={form} layout="vertical" onFinish={handleSend}>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="target_type" label="Hedef Kitle" rules={[{ required: true, message: 'Hedef seçiniz.' }]}>
              <Select placeholder="Kime gönderilecek?" onChange={v => { setTargetType(v); form.setFieldValue('user_ids', undefined); form.setFieldValue('branch_id', undefined); }}>
                {Object.entries(TARGET_LABELS).map(([value, label]) => (
                  <Option key={value} value={value}>{label}</Option>
                ))}
              </Select>
            </Form.Item>

            {targetType && targetType !== 'specific_user' && (
              <Form.Item
                name="branch_id"
                label={targetType === 'branch' ? 'Şube' : 'Şube (İsteğe Bağlı)'}
                rules={targetType === 'branch' ? [{ required: true, message: 'Şube seçiniz.' }] : []}
              >
                <Select placeholder={targetType === 'branch' ? 'Şube seçiniz' : 'Tüm şubeler'} allowClear={targetType !== 'branch'}>
                  {branches.map(b => (
                    <Option key={b.id} value={b.id}>{b.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            {targetType === 'specific_user' && (
              <Form.Item name="user_ids" label="Kullanıcı(lar)" rules={[{ required: true, message: 'En az bir kullanıcı seçiniz.' }]}>
                <UserSearchSelect
                  mode="multiple"
                  allowClear
                  placeholder="Kullanıcı ara (birden fazla seçebilirsiniz)..."
                  renderLabel={(u) => `${u.name} ${u.surname} — ${u.role}`}
                />
              </Form.Item>
            )}
          </div>

          <Form.Item name="title" label="Başlık" rules={[{ required: true, message: 'Başlık gerekli.' }]}>
            <Input placeholder="Bildirim başlığı" maxLength={255} />
          </Form.Item>

          <Form.Item name="body" label="İçerik" rules={[{ required: true, message: 'İçerik gerekli.' }]}>
            <Input.TextArea rows={3} placeholder="Bildirim içeriği..." />
          </Form.Item>

          <div className="flex justify-end">
            <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={sending}>
              Gönder
            </Button>
          </div>
        </Form>
      </Card>

      <Divider>Gönderim Geçmişi</Divider>

      <div className="mb-4 flex gap-3 flex-wrap">
        <UserSearchSelect
          value={filterUserId}
          onChange={(v) => {
            setFilterUserId(v);
            fetchNotifications(1, { filterUserId: v });
          }}
          allowClear
          style={{ width: 240 }}
          placeholder="Alıcıya göre ara (en az 2 karakter)..."
        />
        <RangePicker
          format="DD.MM.YYYY HH:mm"
          showTime={{
            format: 'HH:mm',
            defaultValue: [dayjs('00:00:00', 'HH:mm:ss'), dayjs('23:59:59', 'HH:mm:ss')],
          }}
          onChange={(dates) => {
            setDateRange(dates);
            fetchNotifications(1, { dateRange: dates });
          }}
          placeholder={['Başlangıç', 'Bitiş']}
        />
        <Input.Search
          allowClear
          style={{ width: 260 }}
          placeholder="Başlık/içerikte ara..."
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.target.value)}
          onSearch={(v) => fetchNotifications(1, { filterSearch: v })}
        />
      </div>

      <Table
        columns={columns}
        dataSource={notifications}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={{
          ...pagination,
          onChange: (page, pageSize) => {
            // AntD onChange'i sayfa boyutu degisiminde de tetikliyor - eskiden
            // ikinci parametre yok sayilip per_page sabit 15 gonderiliyordu,
            // bu yuzden "50 / sayfa" secmek hicbir sey yapmiyordu.
            if (pageSize !== pagination.pageSize) {
              setPersistedPageSize(pageSize);
              fetchNotifications(1, {}, pageSize);
            } else {
              fetchNotifications(page, {}, pageSize);
            }
          },
          showTotal: total => `Toplam ${total} bildirim`,
        }}
      />
    </div>
  );
};

export default NotificationSendTab;
